import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { PageSectionsProvider } from './contexts/PageSectionsContext';
import { useTranslation } from 'react-i18next';


// Components
import Layout from './components/Layout/Layout';
import ScrollToTop from './components/Common/ScrollToTop';
import HomePage from './pages/HomePage';
import ToursPage from './pages/ToursPage';
import TourDetailPage from './pages/TourDetailPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import BookingHistoryPage from './pages/BookingHistoryPage';
import NotFoundPage from './pages/NotFoundPage';
import RegisterPage from './pages/RegisterPage';
import FAQPage from './pages/FAQPage';
import BookingTermsPage from './pages/BookingTermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CancellationRefundPage from './pages/CancellationRefundPage';
import HelpCenterPage from './pages/HelpCenterPage';
import FinishRegistrationPage from './pages/FinishRegistrationPage';
import CustomTourRequestPage from './pages/CustomTourRequestPage';

// Admin Components
import AdminLayout from './components/Admin/AdminLayout';
import ProtectedRoute from './components/Admin/ProtectedRoute';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManageToursPage from './pages/admin/ManageToursPage';
import ManageBlogsPage from './pages/admin/ManageBlogsPage';
import ManageAboutPage from './pages/admin/ManageAboutPage';
import ManageContactPage from './pages/admin/ManageContactPage';
import ManageFAQPage from './pages/admin/ManageFAQPage';
import ManageBookingTermsPage from './pages/admin/ManageBookingTermsPage';
import ManagePrivacyPolicyPage from './pages/admin/ManagePrivacyPolicyPage';
import ManageCancellationRefundPage from './pages/admin/ManageCancellationRefundPage';
import ManageHelpCenterPage from './pages/admin/ManageHelpCenterPage';
import ManageFooterPage from './pages/admin/ManageFooterPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import ManageSEOPage from './pages/admin/ManageSEOPage';
import ManageSiteSettingsPage from './pages/admin/ManageSiteSettingsPage';
import ManageBookingsPage from './pages/admin/ManageBookingsPage';
import ManageHomePagePage from './pages/admin/ManageHomePagePage';
import GoogleAnalytics from './components/Common/GoogleAnalytics';
import ManageCustomTourRequestsPage from './pages/admin/ManageCustomTourRequestsPage';
import ManageMessagesPage from './pages/admin/ManageMessagesPage';
import SEOTestPage from './pages/admin/SEOTestPage';



// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#8b9df0',
      dark: '#5a6fd8',
    },
    secondary: {
      main: '#764ba2',
      light: '#9b59b6',
      dark: '#6a4190',
    },
    background: {
      default: '#f5f7fa',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#7f8c8d',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 200,
      fontSize: '3.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 200,
      fontSize: '3rem',
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: '2.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: '2rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    body1: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: '1.1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 400,
          fontSize: '1rem',
          padding: '12px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          backgroundColor: '#005BBD',
          color: 'white',
          '&:hover': {
            backgroundColor: '#004A9F',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 1)',
            },
            '&.Mui-focused': {
              background: 'rgba(255, 255, 255, 1)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 400,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
  },
});

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.dir();
  }, [i18n, i18n.language]);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Router>
          {/* Scroll restoration */}
          <ScrollToTop />

          {/* Google Analytics */}
          <GoogleAnalytics />

          {/* Kimlik doğrulama sağlayıcısı */}
          <AuthProvider>
            <PageSectionsProvider>
            <Routes>
              {/* Genel Site Rotaları */}
              <Route element={<Layout><Outlet /></Layout>}>
                <Route index element={<HomePage />} />
                <Route path="tours" element={<ToursPage />} />
                <Route path="tours/:id" element={<TourDetailPage />} />
                <Route path="blog" element={<BlogPage />} />
                <Route path="blog/:id" element={<BlogDetailPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="faq" element={<FAQPage />} />
                <Route path="booking-terms" element={<BookingTermsPage />} />
                <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="cancellation-refund" element={<CancellationRefundPage />} />
                <Route path="help-center" element={<HelpCenterPage />} />
                <Route path="finish-registration" element={<FinishRegistrationPage />} />
                <Route path="/custom-tour-request" element={<CustomTourRequestPage />} />
                {/* Kullanıcı */}
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="booking-history" element={<BookingHistoryPage />} />

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              {/* Admin Alanı */}
              <Route path="admin" element={<ProtectedRoute><AdminLayout><Outlet /></AdminLayout></ProtectedRoute>}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="tours" element={<ManageToursPage />} />
                <Route path="blogs" element={<ManageBlogsPage />} />
                <Route path="bookings" element={<ManageBookingsPage />} />
                <Route path="users" element={<ManageUsersPage />} />
                <Route path="homepage" element={<ManageHomePagePage />} />
                <Route path="seo" element={<ManageSEOPage />} />
                <Route path="settings" element={<ManageSiteSettingsPage />} />
                <Route path="about" element={<ManageAboutPage />} />
                <Route path="contact" element={<ManageContactPage />} />
                <Route path="faq" element={<ManageFAQPage />} />
                <Route path="booking-terms" element={<ManageBookingTermsPage />} />
                <Route path="privacy-policy" element={<ManagePrivacyPolicyPage />} />
                <Route path="cancellation-refund" element={<ManageCancellationRefundPage />} />
                <Route path="help-center" element={<ManageHelpCenterPage />} />
                <Route path="footer" element={<ManageFooterPage />} />
                <Route path="/admin/custom-tour-requests" element={
                  <ProtectedRoute>
                    <ManageCustomTourRequestsPage />
                  </ProtectedRoute>
                } />
                <Route path="messages" element={<ManageMessagesPage />} />
                <Route path="seo-test" element={<SEOTestPage />} />

                {/* Diğer admin sayfaları buraya ekleyebilirsiniz */}
              </Route>
            </Routes>
            </PageSectionsProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
